import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryStates,
} from 'nuqs'
import { useEffect, useMemo } from 'react'

// Storage keys for different contexts
const CASH_OPERATIONS_LIST_FILTERS_KEY = 'cash-operations-list-filters'

// Define the filter state interface
interface CashOperationFilterState {
  operation: string
  category: string[]
  codeCurrency: string
  dateFrom: string | null
  dateTo: string | null
  amountFrom: string | null
  amountTo: string | null
  areaIds: string[]
  madeById: string | null
  economicCycleId: number | null
}

// Define the saved filters interface (for localStorage)
interface SavedCashOperationFilters {
  operation?: string
  category?: string[]
  codeCurrency?: string
  dateFrom?: string
  dateTo?: string
  amountFrom?: string
  amountTo?: string
  areaIds?: string[]
  madeById?: string
  economicCycleId?: number
}

// Define filter parsers based on CashOperationQueryParams
const cashOperationFilterParsers = {
  // Core filters
  operation: parseAsString.withDefault('all').withOptions({
    clearOnDefault: true,
  }),
  category: parseAsArrayOf(parseAsString, ',')
    .withDefault(['all'])
    .withOptions({
      clearOnDefault: true,
    }),
  type: parseAsArrayOf(parseAsString, ',').withDefault(['all']).withOptions({
    clearOnDefault: true,
  }),
  codeCurrency: parseAsString.withDefault('CUP').withOptions({
    clearOnDefault: true,
  }),

  // Date range
  dateFrom: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  dateTo: parseAsString.withOptions({
    clearOnDefault: true,
  }),

  amountFrom: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  amountTo: parseAsString.withOptions({
    clearOnDefault: true,
  }),

  // Area and user filters
  areaIds: parseAsArrayOf(parseAsString, ',').withDefault([]).withOptions({
    clearOnDefault: true,
  }),
  madeById: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  economicCycleId: parseAsInteger.withOptions({
    clearOnDefault: true,
  }),
}

// Helper function to save filters to localStorage
const saveFiltersToStorage = (
  filters: CashOperationFilterState,
  key: string
) => {
  try {
    // Only save non-default values to reduce storage size
    const filtersToSave = Object.entries(filters).reduce(
      (acc, [filterKey, value]) => {
        // Skip default values
        if (
          value === '' ||
          value === 'all' ||
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0) ||
          (Array.isArray(value) && value.length === 1 && value[0] === 'all') ||
          (filterKey === 'codeCurrency' && value === 'CUP')
        ) {
          return acc
        }
        ;(acc as Record<string, unknown>)[filterKey] = value
        return acc
      },
      {} as SavedCashOperationFilters
    )

    localStorage.setItem(key, JSON.stringify(filtersToSave))
  } catch {
    //
  }
}

// Helper function to get filters from localStorage
const getFiltersFromStorage = (
  key: string
): SavedCashOperationFilters | null => {
  try {
    const stored = localStorage.getItem(key)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

export const useCashOperationFilters = () => {
  // Use nuqs for URL state management
  const [filters, setFilters] = useQueryStates(cashOperationFilterParsers, {
    history: 'replace',
    shallow: true,
  })

  // Load filters from localStorage on mount - simple approach like billing
  useEffect(() => {
    const savedFilters = getFiltersFromStorage(CASH_OPERATIONS_LIST_FILTERS_KEY)
    if (savedFilters) {
      // Merge saved filters with current state, preserving URL parameters
      setFilters((prevFilters) => ({
        ...prevFilters,
        ...savedFilters,
      }))
    }
  }, [setFilters])

  // Save filters to localStorage whenever they change
  useEffect(() => {
    saveFiltersToStorage(filters, CASH_OPERATIONS_LIST_FILTERS_KEY)
  }, [filters])

  // Convert filters to CashOperationQueryParams format
  const searchParams = useMemo(() => {
    const params = new URLSearchParams()

    if (filters.operation && filters.operation !== 'all') {
      params.set('operation', filters.operation)
    }

    if (!filters.category.includes('all')) {
      params.set('category', filters.category.join(','))
    }

    if (filters.codeCurrency !== 'CUP') {
      params.set('codeCurrency', filters.codeCurrency)
    }

    if (filters.dateFrom) {
      params.set('dateFrom', filters.dateFrom)
    }

    if (filters.dateTo) {
      params.set('dateTo', filters.dateTo)
    }

    if (filters.amountFrom) {
      params.set('amountFrom', filters.amountFrom)
    }

    if (filters.amountTo) {
      params.set('amountTo', filters.amountTo)
    }

    if (filters.areaIds.length > 0) {
      params.set('areaIds', filters.areaIds.join(','))
    }

    if (filters.madeById) {
      params.set('madeById', filters.madeById.toString())
    }

    if (filters.economicCycleId) {
      params.set('economicCycleId', filters.economicCycleId.toString())
    }

    return params.toString()
  }, [filters])

  const restoreListFilters = () => {
    const savedListFilters = getFiltersFromStorage(
      CASH_OPERATIONS_LIST_FILTERS_KEY
    )
    if (savedListFilters) {
      setFilters(savedListFilters)
    }
  }

  // Helper functions for updating specific filters
  const updateOperation = (operation: string) => {
    setFilters((prev) => ({ ...prev, operation }))
  }

  const updateCategory = (categories: string[]) => {
    setFilters((prev) => ({ ...prev, category: categories }))
  }

  const updateCurrency = (currency: string) => {
    setFilters((prev) => ({ ...prev, codeCurrency: currency }))
  }

  const updateDateRange = (dateFrom: string | null, dateTo: string | null) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo }))
  }

  const updateAmountRange = (
    amountFrom: string | null,
    amountTo: string | null
  ) => {
    setFilters((prev) => ({ ...prev, amountFrom, amountTo }))
  }

  const updateArea = (areaIds: string[]) => {
    setFilters((prev) => ({ ...prev, areaIds }))
  }

  const updateMadeById = (madeById: string | null) => {
    setFilters((prev) => ({ ...prev, madeById }))
  }

  const updateEconomicCycle = (economicCycleId: number | null) => {
    setFilters((prev) => ({ ...prev, economicCycleId }))
  }

  // Individual clear functions
  const clearOperationFilter = () => {
    setFilters((prev) => ({ ...prev, operation: 'all' }))
  }

  const clearCategoryFilter = () => {
    setFilters((prev) => ({ ...prev, category: ['all'] }))
  }

  const clearCurrencyFilter = () => {
    setFilters((prev) => ({ ...prev, codeCurrency: 'CUP' }))
  }

  const clearDateFilter = () => {
    setFilters((prev) => ({ ...prev, dateFrom: null, dateTo: null }))
  }

  const clearAmountFilter = () => {
    setFilters((prev) => ({ ...prev, amountFrom: null, amountTo: null }))
  }

  const clearAreaFilter = () => {
    setFilters((prev) => ({ ...prev, areaIds: [] }))
  }

  const clearMadeByFilter = () => {
    setFilters((prev) => ({ ...prev, madeById: null }))
  }

  const clearEconomicCycleFilter = () => {
    setFilters((prev) => ({ ...prev, economicCycleId: null }))
  }

  // Remove individual items from array filters
  const removeFromArrayFilter = (
    key: keyof CashOperationFilterState,
    valueToRemove: string
  ) => {
    setFilters((prev) => {
      const currentArray = prev[key] as string[]
      const newArray = currentArray.filter((item) => item !== valueToRemove)
      return { ...prev, [key]: newArray }
    })
  }

  const clearAllFilters = () => {
    const clearedFilters: CashOperationFilterState = {
      operation: 'all',
      category: ['all'],
      codeCurrency: 'CUP',
      dateFrom: null,
      dateTo: null,
      amountFrom: null,
      amountTo: null,
      areaIds: [],
      madeById: null,
      economicCycleId: null,
    }

    setFilters(clearedFilters)

    // Also clear localStorage
    try {
      localStorage.removeItem(CASH_OPERATIONS_LIST_FILTERS_KEY)
    } catch {
      //
    }
  }

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      (filters.operation && filters.operation !== 'all') ||
      !filters.category.includes('all') ||
      (filters.codeCurrency && filters.codeCurrency !== 'CUP') ||
      !!filters.dateFrom ||
      !!filters.dateTo ||
      !!filters.amountFrom ||
      !!filters.amountTo ||
      filters.areaIds.length > 0 ||
      !!filters.madeById ||
      !!filters.economicCycleId
    )
  }, [filters])

  return {
    // Current filter values
    filters,
    searchParams,

    // Basic update functions
    updateOperation,
    updateCategory,
    updateCurrency,
    updateDateRange,
    updateAmountRange,
    updateArea,
    updateMadeById,
    updateEconomicCycle,
    removeFromArrayFilter,

    // Individual clear functions
    clearOperationFilter,
    clearCategoryFilter,
    clearCurrencyFilter,
    clearDateFilter,
    clearAmountFilter,
    clearAreaFilter,
    clearMadeByFilter,
    clearEconomicCycleFilter,
    clearAllFilters,

    // Helper states
    hasActiveFilters,
    setFilters,
    restoreListFilters,
  }
}

export type CashOperationFilters = ReturnType<
  typeof useCashOperationFilters
>['filters']
export type CashOperationFilterKey = keyof typeof cashOperationFilterParsers
